import { UpdatePassInput } from '@app/models/pass.model'
import { EXPIRING_REQUIREMENTS_FIELDS } from '@app/utils/requirements-expiring.util'

export const SET_PASS = `
mutation upsert_pass_authorized($data: createPassAllowedInput!) {
    upsert_pass_authorized(data: $data) {
        success
        errorText
        ${EXPIRING_REQUIREMENTS_FIELDS}
        data {
            q_id
        }
    }
}
`

export const SET_PASSES = `
mutation UpsertPassesAuthorized($data: [createPassAllowedInput!]!) {
    upsert_passes_authorized(data: $data) {
        success
        errorText
        ${EXPIRING_REQUIREMENTS_FIELDS}
        data {
            q_id
        }
    }
}
`

export const UPDATE_PASS = `
mutation update_pass_by_id($data: UpdatePassByIdInput!) {
    update_pass_by_id(data: $data) {
        success
        errorText
        ${EXPIRING_REQUIREMENTS_FIELDS}
        data {
            id
            event_id
            time_limitations
            gender
        }
    }
}`

export const UPDATE_SUB_EVENT_PASSES = `
mutation update_sub_event_passes($data: UpdatePassByIdInput!) {
    update_sub_event_passes(data: $data) {
        success
        errorText
        data {
            id
            name
            last_name
        }
    }
}`

export const GET_PASSES = `
query get_passes($data: getPassesInput!,$pageOptions: getPageOptionsInput!) {
    get_passes(
        data: $data,
        pageOptions: $pageOptions
    ) {
        success
        errorText
        data {
            ${Object.keys(new UpdatePassInput()).join('\n')}
            requirements_details {
                id
                name
                status
            }
        }
        pagination {
            totalCount
            currentPage
            totalPages
            hasNextPage
            hasPreviousPage
            itemsPerPage
        }
    }
}
`

export const GET_UNREGISTERED_EVENT_PASSES = `
query get_unregistered_event_passes($event_id: Float!,$sub_event_id: Float) {
    get_unregistered_event_passes(
        event_id: $event_id,
        sub_event_id: $sub_event_id
    ) {
        success
        errorText
        data {
            id
            event_id
            order_number
            sub_event_id
            first_name
            last_name
            mobile
        }
    }
}
`

export const GET_PASSES_BY_EVENT_AND_ORDER = `
query get_passes_by_event_and_order($event_id: Float!,$order_number: String!) {
    get_passes_by_event_and_order(
        event_id: $event_id,
        order_number: $order_number
    ) {
        success
        errorText
        data {
            id
            event_id
            order_number
            sub_event_id
            first_name
            last_name
            sub_sub_event_name
            mobile
            limitations_start
            limitations_end
        }
    }
}
`

export const GET_PASSES_BY_C_IDS = `
query get_passes_by_c_ids($c_ids: [Float!]!) {
    get_passes_by_c_ids(c_ids: $c_ids) {
        id
        q_id
        first_name
        last_name
        name
        mobile
        email
        country_code
        id_number
        event_id
        client_id
        category
        status
        allowed
        transferable
        limitations_start
        limitations_end
        time_limitations
        areas
        order_number
        secured
        single_uses
        default_single_uses
        date_of_birth
        assignable_client_roles
    }
}
`

export const CHECK_ACTIVE_PASS_FOR_MOBILE = `
query CheckActivePassForMobile($input: CheckActivePassInput!) {
  check_active_pass_for_mobile(input: $input) {
    success
    message
    data {
      exists
      passes {
        id
        name
        last_name
        event_name
        status
        created_time
      }
    }
  }
}
`

export const GET_DUPLICATE_ACTIVE_PASSES_REPORT = `
query GetDuplicateActivePassesReport($input: DuplicatePassesReportInput!) {
  get_duplicate_active_passes_report(input: $input) {
    success
    message
    data {
      total_groups
      page
      total_pages
      groups {
        mobile
        country_code
        name
        last_name
        pass_count
        passes {
          id
          event_id
          event_name
          client_role_name
          created_time
        }
      }
    }
  }
}
`

export const GET_PEOPLE_INSIDE_EVENT = `
query get_people_inside_event($event_id: Float!, $minutes: Float, $page: PeopleInsidePageInput) {
    get_people_inside_event(event_id: $event_id, minutes: $minutes, page: $page) {
        success
        errorText
        total
        next_cursor
        data {
            pass_id
            first_name
            last_name
            mobile
            country_code
            category
            type_specific_data
            areas
            area_names
            client_role_name
            id_image_url
            id_images {
                url
                is_identification
                title
            }
            entry_time
            creator_pass_id
            creator_first_name
            creator_last_name
            id_number
            date_of_birth
            id_card_expiration_date
            gender
            station_name
            company
            vehicle_number
        }
    }
}
`

/**
 * The vehicle/HME passes a person owns (made from their requirement). PAPI cancels them with the
 * person and moves them to the person's dates, so the portal says so before it saves either.
 */
export const GET_OWNED_ASSET_PASSES = `
query get_owned_asset_passes($pass_id: Float!) {
    get_owned_asset_passes(pass_id: $pass_id) {
        success
        errorText
        data {
            id
            category
            id_number
        }
    }
}
`

/**
 * Hydrate ONE complete pass by id, for surfaces that hold a pass id but not a pass — the entry
 * monitor's cards, which carry a display-only projection (see GET_PEOPLE_INSIDE_EVENT above).
 *
 * `get_passes` has no id filter and `get_pass_by_id` is PCAPI-token gated (and rewrites areas to
 * names), so this connections query is the only id-addressed route to a whole pass. The field set
 * is deliberately `UpdatePassInput` — byte-identical to GET_PASSES — so a modal opened from a card
 * receives exactly the model shape it receives from a list row. Anything omitted here is a field
 * the edit form renders empty and then writes back empty on save.
 *
 * `type_specific_data` and `connections` are appended because vehicle/HME passes need them and
 * they are not part of the person-pass input model.
 */
export const GET_PASS_WITH_CONNECTIONS = `
query getPassWithConnections($passId: Float!) {
    get_pass_with_connections(passId: $passId) {
        success
        message
        errorText
        data {
            primary_pass {
                ${Object.keys(new UpdatePassInput()).join('\n                ')}
                type_specific_data
            }
            connections {
                id
                connection_type
                metadata
                connected_pass {
                    id
                    first_name
                    last_name
                    mobile
                    country_code
                }
            }
            total_connections
        }
    }
}
`
